import z from 'zod';
/**
 * **Backward-compatibility window (0.48.x):** the source-attribution fields
 * (`documentId`, `pageIds`, per-event `pageId`) are `.optional()` in the schema
 * so consumers can parse historical payloads emitted before 0.48.0. The TS
 * type below keeps them required because new producer payloads always include
 * them. After all consumers ship a build that handles the new fields, tighten
 * the schema to required in a follow-up release.
 */
const engineHistoryPointSchema = z.looseObject({
    date: z.string().optional(),
    tsn: z.number(),
    csn: z.number(),
});
const engineHistoryCoverageSchema = z.looseObject({
    documentId: z.string(),
    pageIds: z.array(z.string()),
    role: z.enum(['primary', 'extension', 'restatement']),
    operator: z.string().optional(),
    asOfDate: z.string().optional(),
    firstEvent: engineHistoryPointSchema.optional(),
    lastEvent: engineHistoryPointSchema.optional(),
    rowCount: z.number(),
    contributedCount: z.number(),
});
const engineHistoryGapEndSchema = z.looseObject({
    date: z.string().optional(),
    tsn: z.number(),
    csn: z.number(),
    type: z.string(),
    documentId: z.string().optional(),
    pageId: z.string(),
});
const engineHistoryGapSchema = z.looseObject({
    from: engineHistoryGapEndSchema,
    to: engineHistoryGapEndSchema,
    hours: z.number(),
    cycles: z.number(),
    crossesDocuments: z.boolean(),
});
const engineHistoryConflictSchema = z.looseObject({
    documentId: z.string().optional(),
    pageId: z.string(),
    type: z.string(),
    date: z.string().optional(),
    tsn: z.number(),
    csn: z.number(),
    coveredBy: z.string(),
    // DR-4628: the conflicting row's own identity, plus which duplicate checks failed.
    // Additive and optional. `.catch(undefined)` so one bad token does not drop every conflict.
    aircraftRegistration: z.string().optional().catch(undefined),
    aircraftMsn: z.string().optional().catch(undefined),
    position: z.string().optional().catch(undefined),
    operator: z.string().optional().catch(undefined),
    reasons: z.array(z.enum(['date', 'aircraft', 'tsn', 'csn'])).optional().catch(undefined),
    // DR-5086: a newer, at-least-as-strongly signed sheet replaced this row. Additive; only `true`.
    superseded: z.literal(true).optional().catch(undefined),
});
// The graph contract declares these unions as TypeScript types only (no runtime const), so the
// literals are repeated here; `engine-events-ready.webhook.test.ts` fails the compile when the
// graph period and this schema drift apart.
const OPERATING_PERIOD_REGIONS = [
    'europe',
    'north-america',
    'south-america',
    'japan-korea',
    'china',
    'south-asia',
    'south-east-asia',
    'gulf',
    'middle-east',
    'north-africa',
    'sub-saharan-africa',
    'oceania',
    'russia-cis',
];
const OPERATING_PERIOD_ENVIRONMENT_CLASSES = ['benign', 'harsh', 'hot-harsh'];
/** DR-5869: Oil's Vulcan environment classes, Vulcan's own tokens (note the space in `HOT HARSH`). */
export const VULCAN_ENVIRONMENTS = ['BENIGN', 'HOT', 'HARSH', 'HOT HARSH', 'NOT ASSESSED'];
// DR-5869: Brain's own vocabularies (region, class, basis) take a field-level `.catch`, as the
// engine row's enums do: the graph contract may add a member, and a consumer on an older package
// must lose that one value, not the payload. Brain emits only members of its own types, so a parse
// changes nothing and the forecast manifest's hash parity holds. `vulcanEnvironment` is Oil's own
// vocabulary and stays strict; so do the shape fields: an invalid period is an envelope violation.
const operatingPeriodDeparturesSchema = z.looseObject({
    region: z.enum([...OPERATING_PERIOD_REGIONS, 'unknown']).catch('unknown'),
    environmentClass: z.enum(OPERATING_PERIOD_ENVIRONMENT_CLASSES).optional().catch(undefined),
    count: z.number(),
});
/** DR-5995: also the period element of `project-data-ready`'s `operatingPeriods`. */
export const engineOperatingPeriodSchema = z.looseObject({
    from: z.string(),
    to: z.string().optional(),
    operator: z.string(),
    eventNodeId: z.string(),
    region: z.enum(OPERATING_PERIOD_REGIONS).optional().catch(undefined),
    environmentClass: z.enum(OPERATING_PERIOD_ENVIRONMENT_CLASSES).optional().catch(undefined),
    classBasis: z.enum(['region-based', 'route-based']).optional().catch(undefined),
    departures: z.array(operatingPeriodDeparturesSchema).optional(),
    vulcanEnvironment: z.enum(VULCAN_ENVIRONMENTS),
});
export const engineEventsReadyWebhookSchema = z.looseObject({
    type: z.literal('engine-events-ready'),
    projectId: z.string(),
    // `z.iso.datetime()`, not a bare string: the consumer contract is an ORDERABLE instant,
    // so the schema enforces what the field documents. Follows `pma-der-events-ready` /
    // `document-header-ready` rather than the looser `project-progress` / `project-data-ready`
    // spelling.
    //
    // `.optional()` is a ROLLOUT concession, not a statement that the stamp is dispensable —
    // Brain always emits it (see the family's builder, pinned by its test). This schema is a
    // member of `webhookPayloadSchema`, so a REQUIRED field would make a consumer that
    // upgrades `@avisoma/shared` reject every older, unstamped envelope still in flight — and reject
    // ALL of them for as long as the consumer runs ahead of Brain's own deploy. Tighten to
    // required in a later release, once no unstamped envelope can still be in flight (DR-3278).
    generatedAt: z.iso.datetime().optional(),
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    engineType: z.string().nullable(),
    esn: z.string().nullable(),
    tsn: z.number().nullable(),
    csn: z.number().nullable(),
    tslsv: z.number().nullable(),
    cslsv: z.number().nullable(),
    tslsvUncorroborated: z.literal(true).optional(),
    date: z.string().nullable(),
    documentId: z.string().optional(),
    pageIds: z.array(z.string()).optional(),
    engineEvents: z.array(z.looseObject({
        type: z.string(),
        pageId: z.string().optional(),
        date: z.string().optional(),
        // DR-3187: shop-visit rows often have no printed rating. Absence is the "document did not
        // state it" reading — same as `movement`. Oil stores `sanitize(event.thrust) ?? null`.
        // `.catch(undefined)` is the consumer-side tolerance Oil asked for: a stray `null` or
        // non-string must not fail the whole payload parse. Brain does not emit `null` on purpose.
        thrust: z.string().optional().catch(undefined),
        msn: z.string().optional(),
        tsn: z.number(),
        csn: z.number(),
        reason: z.string().optional(),
        operator: z.string().optional(),
        // DR-3503: how `operator` was obtained. Additive and OPTIONAL — absence is "no operator"
        // or a historical payload. `.catch(undefined)` so an out-of-vocabulary token does not
        // fail the whole payload parse. Vocabulary is the extraction sanitiser's job.
        operatorSource: z.enum(['model', 'document-propagated', 'carried']).optional().catch(undefined),
        aircraftReg: z.string().optional(),
        // DR-5363: the printed aircraft model, verbatim. Additive and OPTIONAL.
        aircraftType: z.string().optional(),
        position: z.string().optional(),
        // DR-3269: the physical-movement axis, additive and OPTIONAL — absence is the field's
        // "document did not state it" reading, so a historical payload that carries no movement at
        // all parses exactly as before.
        //
        // The `.catch(undefined)` is the tolerance the axis requires: an out-of-vocabulary token must
        // not fail validation, because failing it would strand a whole engine's history over an enum
        // typo. Note where the value actually goes: the producer validates and then ships its own
        // unparsed payload, so a bad token still egresses as printed — it is dropped to `undefined` on
        // the CONSUMER side, whenever a reader parses through this schema on shared >= 0.212.0.
        // Vocabulary at the source is the extraction sanitiser's job (`sanitiseRowMovement`).
        movement: z.enum(['installation', 'removal', 'none']).optional().catch(undefined),
        // DR-4427: the representative document of the entity that produced this row. Additive and
        // OPTIONAL — a historical payload carries only `pageId`.
        documentId: z.string().optional(),
        // DR-4427: other documents that printed the same event. `.catch(undefined)` so a malformed
        // provenance list can never cost the row.
        sources: z
            .array(z.looseObject({ documentId: z.string(), pageId: z.string() }))
            .optional()
            .catch(undefined),
        // DR-4484: a shop-visit promoted on the operator log's own re-install remark that no
        // same-moment shop-visit row from another engine-history document has confirmed. Additive
        // and OPTIONAL; `.catch(undefined)` so a corrupt token never costs the row.
        uncorroborated: z.literal(true).optional().catch(undefined),
        // DR-4751: this shop visit is the engine history's own removal row (it keeps the removal's
        // timeline role); corroboration clears `uncorroborated`, never this. Additive and OPTIONAL;
        // `.catch(undefined)` so a corrupt token never costs the row.
        operatorLogRemoval: z.literal(true).optional().catch(undefined),
        // DR-5177: every document behind this merged I/R row is an explicitly unsigned sheet, and a
        // signed history also covers the engine. Additive and OPTIONAL, never `false`;
        // `.catch(undefined)` so a corrupt token never costs the row.
        unsigned: z.literal(true).optional().catch(undefined),
    })),
    // DR-4427: per-document coverage, gap markers and covered conflicts of the merged timeline. All
    // additive and OPTIONAL. `.catch(undefined)` on each: the producer throws on any envelope-level
    // violation, so without it one malformed entry would strand the whole engine family.
    documents: z.array(engineHistoryCoverageSchema).optional().catch(undefined),
    gaps: z.array(engineHistoryGapSchema).optional().catch(undefined),
    conflicts: z.array(engineHistoryConflictSchema).optional().catch(undefined),
    // DR-5869: additive and OPTIONAL; see `WebhookEngineEventsReady.operatingPeriods`.
    operatingPeriods: z.array(engineOperatingPeriodSchema).optional(),
});
//# sourceMappingURL=engine-events-ready.webhook.js.map